import { Gavel } from "@phosphor-icons/react";

/**
 * EngagementDecisions — canonical decision log for an engagement.
 *
 * Iter 85.32 · Populated by the Briefing Studio when an
 * engagement_briefing `decision` proposal is accepted. Each entry mirrors
 * the source note's `meeting_structured.decisions[]` and carries
 * provenance (`source_note_id`, `source_proposal_id`) so the audit trail
 * stays intact. Empty state hides the block entirely so pre-briefing
 * engagements don't accumulate visual noise.
 */
export default function EngagementDecisions({ engagement }) {
  const decisions = Array.isArray(engagement?.decisions) ? engagement.decisions : [];
  if (decisions.length === 0) return null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="border border-border bg-card mb-6" data-testid="engagement-decisions">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2 text-primary">
          <Gavel size={16} weight="bold" />
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-eyebrow">Decision log</div>
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          {decisions.length} recorded
        </div>
      </div>
      <div className="divide-y divide-border">
        {decisions.map((d, idx) => (
          <div
            key={d.source_proposal_id || `${idx}-${d.text?.slice(0, 12)}`}
            className="px-6 py-4"
            data-testid={`engagement-decision-${idx}`}
          >
            <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{d.text}</div>
            <div className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>Decided {fmtDate(d.date || d.created_at)}</span>
              {d.owner && <span>Owner: {d.owner}</span>}
              {d.source_note_id && (
                <a
                  href={`/notes?open=${d.source_note_id}`}
                  className="text-primary hover:underline"
                  data-testid={`engagement-decision-${idx}-source`}
                >
                  Source briefing →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
